import { useState } from "react";
import { getData } from "@/lib/utils";
import api from "@/store/api";

const useStore = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAllStores = async () => {
    try {
      const response = await api.get("stores");
      return response.data.data;
    } catch (err: any) {
      console.error(err);
    }
  };
  const updateStore = async (storeData: any, id: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }
      const formData = new FormData();

      formData.append("bioEn", storeData?.bioEn || "");
      formData.append("bioAr", storeData?.bioAr || "");

      if (storeData.imgUrl instanceof File) {
        formData.append("imgUrl", storeData.imgUrl, storeData.imgUrl.name);
      } else if (
        typeof storeData.imgUrl === "string" &&
        !storeData.imgUrl.startsWith("https://res.cloudinary.com")
      ) {
        formData.append("imgUrl", storeData.imgUrl);
      }

      if (storeData.coverUrl instanceof File) {
        formData.append(
          "coverUrl",
          storeData.coverUrl,
          storeData.coverUrl.name
        );
      } else if (
        typeof storeData.coverUrl === "string" &&
        !storeData.coverUrl.startsWith("https://res.cloudinary.com")
      ) {
        formData.append("coverUrl", storeData.coverUrl);
      }
      const response = await api.patch(`stores/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${Token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setIsLoading(false);
      return response.data;
    } catch (err: any) {
      console.error(err);

      setError(
        err.response?.data?.message ||
          "Failed to update the store. Please try again."
      );
    }
  };
  const createStore = async (storeData: any) => {
    try {
      setIsLoading(true);
      setError(null);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }

      const formData = new FormData();

      formData.append("bioEn", storeData?.bioEn || "");
      formData.append("bioAr", storeData?.bioAr || "");

      if (storeData.imgUrl instanceof File) {
        formData.append("imgUrl", storeData.imgUrl, storeData.imgUrl.name);
      } else if (
        typeof storeData.imgUrl === "string" &&
        !storeData.imgUrl.startsWith("https://res.cloudinary.com")
      ) {
        formData.append("imgUrl", storeData.imgUrl);
      }

      if (storeData.coverUrl instanceof File) {
        formData.append(
          "coverUrl",
          storeData.coverUrl,
          storeData.coverUrl.name
        );
      } else if (
        typeof storeData.coverUrl === "string" &&
        !storeData.coverUrl.startsWith("https://res.cloudinary.com")
      ) {
        formData.append("coverUrl", storeData.coverUrl);
      }

      const response = await api.post("stores", formData, {
        headers: {
          Authorization: `Bearer ${Token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (err: any) {
      console.error(err);

      setError(
        err.response?.data?.message ||
          "Failed to create the store. Please try again."
      );
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  const getStoreById = async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await api.get(`stores/public/${id}`);
      return response.data.data;
    } catch (err: any) {
      console.error(err);
    }
  };
  const getMyStore = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }
      const response = await api.get("stores/user", {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });
      return response.data;
    } catch (err: any) {
      console.error(err);
      setError("Failed to fetch store data. Please try again.");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  const deleteStore = async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const Token = getData("tokens")?.accessToken;
      if (!Token) {
        throw new Error("Authentication token is missing");
      }
      const response = await api.delete(`stores/${id}`, {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });
      return response.data;
    } catch (err: any) {
      console.error(err);

      setError("Failed to delete store. Please try again.");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  return {
    createStore,
    updateStore,
    deleteStore,
    getMyStore,
    getStoreById,
    isLoading,
    error,
    getAllStores,
  };
};

export default useStore;
